import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './pages/LoginPage';
import BootstrapOrgPage from './pages/BootstrapOrgPage';
import AppShell from './pages/AppShell';
import HomeView from './views/HomeView';
import OrgView from './views/Org/OrgView';
import ProfileView from './views/ProfileView';
import LegacyProfileRedirect from './pages/LegacyProfileRedirect';
import OnboardingView from './views/OnboardingView';
import TimeOffView from './views/TimeOffView';
import TimeView from './views/timeView/TimeView';
import SettingsView from './views/SettingsView';
import { NotFound } from './components/NotFound';

// Docs view is lazy-imported so the component loads only when the
// user navigates to /docs. T-207 stub; fleshed out in T-211.
const DocsView = lazy(() => import('./views/docs/DocsView'));

// Analytics view is lazy-imported so the recharts-heavy bundle
// (loaded transitively when T-098 fleshes out the dashboard) stays
// out of the initial download for non-Analytics page loads. The
// per-page Suspense fallback (`<AnalyticsSkeleton />`) lands in
// T-097 — same shape as the eventual dashboard so the chunk swap
// doesn't flash a stub before the real grid.
const AnalyticsView = lazy(() => import('./views/analytics/AnalyticsView'));
const AnalyticsSkeleton = lazy(() =>
  import('./views/analytics/AnalyticsSkeleton').then((m) => ({
    default: m.AnalyticsSkeleton,
  })),
);

// Setup Organization — T-124 + T-126 (org-setup-flow-implementation-plan
// §7 + §9). Replaces the older /settings/data-import surface; we keep
// that route alive as a redirect (spec §11) so any existing in-product
// links stay working. <SetupOrgRoute> (T-126) stacks <SetupOrgPanel>
// (the 4-state UI machine from spec §5) on top of <DeleteOrgPanel>
// (OWNER-only danger zone from spec §4 + §9.2) so both destructive
// surfaces share one canonical URL.
const SetupOrgRoute = lazy(
  () => import('./views/settings/SetupOrgRoute'),
);

// Post-delete landing page (T-125, org-setup-flow-spec.md §9.2).
// Registered OUTSIDE the auth/org gates because the user has just
// signed out + their organization no longer exists.
const FarewellPage = lazy(
  () => import('./views/farewell/FarewellPage'),
);

// Invitee landing page (T-257, user-onboarding-spec.md §7).
// Registered OUTSIDE the auth/org gates because invitees haven't
// signed in yet and aren't members of an org. The page itself
// handles peek + Google sign-in + auto-accept.
const InvitePage = lazy(
  () => import('./pages/InvitePage'),
);

// First-login welcome wizard (T-254, user-onboarding-spec.md §8).
// Rendered AFTER /invite/<token> success — the user has a session +
// is in an org at this point. Outside the AppShell to keep the
// canvas clean for the moment.
const WelcomePage = lazy(
  () => import('./pages/WelcomePage'),
);

// Settings → Team members (T-253, user-onboarding-spec.md §6).
// Inside the AppShell — lives at /settings/team-members. OWNER +
// ADMIN only; MEMBER sees a 403 banner inside the panel.
const TeamMembersPanel = lazy(
  () => import('./views/settings/TeamMembersPanel'),
);

// Settings → Data export (T-266). Inside the AppShell — lives at
// /settings/data-export. OWNER + ADMIN only.
const DataExportPanel = lazy(
  () => import('./views/settings/DataExportPanel'),
);

// /settings/slack. OWNER + ADMIN only. T-270 — Slack OAuth + install/uninstall.
const SlackPanel = lazy(
  () => import('./views/settings/SlackPanel'),
);

// /reviews/:id — review fill page (T-280). Inside the AppShell.
const ReviewFillPage = lazy(
  () => import('./views/reviews/ReviewFillPage').then((m) => ({
    default: m.ReviewFillPage,
  })),
);

// /reviews — review admin overview (T-281). Inside the AppShell.
const ReviewsAdminView = lazy(
  () => import('./views/reviews/ReviewsAdminView'),
);

// /settings/review-cycles — review cycles admin panel (T-281). Inside the AppShell.
const ReviewCyclesPanel = lazy(
  () => import('./views/settings/ReviewCyclesPanel'),
);

// /contracts/:id/sign — native click-to-sign page (T-314/T-315).
// Inside the AppShell: signing requires login (spec §2).
const ContractSignPage = lazy(
  () => import('./views/contracts/ContractSignPage'),
);

// /settings/contracts — employment-contract templates + the Google
// Workspace connection (T-311/T-312). Inside the AppShell. OWNER +
// ADMIN only; MEMBER sees a restricted notice inside the panel.
const ContractsPanel = lazy(
  () => import('./views/settings/ContractsPanel'),
);

// /m/approvals — mobile manager approval inbox (T-286). Inside the AppShell
// but rendered by the MobileSimpleShell short-circuit on phone viewports.
// OWNER/ADMIN only; MEMBERs redirect to / on mount.
const MobileApprovalsPage = lazy(
  () => import('./views/mobile/MobileApprovalsPage'),
);

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>
  );
}

export default function App() {
  const { loading, me } = useAuth();

  if (loading) return <Loader />;

  // T-257 — /invite/<token> must be reachable in EVERY auth state:
  // signed-out (the common case — invitee clicks the email link
  // fresh), signed-in-without-org (rare — somehow had a stale
  // session before clicking), and signed-in-with-org (rare — clicks
  // the link from inside the app). InvitePage's state machine
  // handles all three via /peek + /accept. Render it before the
  // auth/org gating so it bypasses the /login + /bootstrap
  // redirects.
  const invitePageRoute = (
    <Route
      path="/invite/:token"
      element={
        <Suspense fallback={<Loader />}>
          <InvitePage />
        </Suspense>
      }
    />
  );

  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/*
         * /farewell must be reachable while signed-out — the
         * DeleteOrgPanel flow lands here AFTER POST /auth/logout
         * (org-setup-flow-spec.md §9.2). Falls back through to the
         * /login redirect otherwise.
         */}
        <Route
          path="/farewell"
          element={
            <Suspense fallback={<Loader />}>
              <FarewellPage />
            </Suspense>
          }
        />
        {invitePageRoute}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  if (!me.organization) {
    return (
      <Routes>
        <Route path="/bootstrap" element={<BootstrapOrgPage />} />
        {invitePageRoute}
        <Route path="*" element={<Navigate to="/bootstrap" replace />} />
      </Routes>
    );
  }
  return (
    <Routes>
      {/* /invite/<token> renders outside the AppShell even when the
       * caller is signed-in-and-in-an-org (the page detects this via
       * useAuth and either accepts immediately or shows the
       * already-in-org panel). Registered ahead of the shell-wrapped
       * routes so it wins the URL match. */}
      {invitePageRoute}
      {/* /welcome — first-login wizard (T-254). Outside the AppShell
       * to keep the moment clean. WelcomePage hard-redirects to /
       * if `firstSeenAt` is already set, so it stays one-shot. */}
      <Route
        path="/welcome"
        element={
          <Suspense fallback={<Loader />}>
            <WelcomePage />
          </Suspense>
        }
      />

      <Route element={<AppShell />}>
        <Route index element={<HomeView />} />
        <Route path="/employees" element={<OrgView />} />
        <Route path="/employees/:id" element={<ProfileView />} />
        {/*
         * Legacy profile route (T-325). Every server-produced "View in
         * PeopleAwesomeness" link wrote `/profile/{employeeId}` until
         * T-325, and this app has never served that path, so those links
         * 404'd. The server now writes `/employees/:id`, but the old
         * links are already in Slack messages and inboxes and cannot be
         * rewritten, so the path stays alive as a redirect.
         */}
        <Route path="/profile/:id" element={<LegacyProfileRedirect />} />
        <Route path="/onboarding" element={<OnboardingView />} />
        <Route path="/time-off" element={<TimeOffView />} />
        <Route path="/time" element={<TimeView />} />
        <Route
          path="/reviews"
          element={
            <Suspense fallback={<Loader />}>
              <ReviewsAdminView />
            </Suspense>
          }
        />
        <Route
          path="/reviews/:reviewId"
          element={
            <Suspense fallback={<Loader />}>
              <ReviewFillPage />
            </Suspense>
          }
        />
        <Route
          path="/docs"
          element={
            <Suspense fallback={<Loader />}>
              <DocsView />
            </Suspense>
          }
        />
        <Route
          path="/analytics"
          element={
            <Suspense fallback={<AnalyticsSkeleton />}>
              <AnalyticsView />
            </Suspense>
          }
        />
        <Route
          path="/m/approvals"
          element={
            <Suspense fallback={<Loader />}>
              <MobileApprovalsPage />
            </Suspense>
          }
        />
        <Route path="/settings" element={<SettingsView />} />
        <Route
          path="/settings/setup-organization"
          element={
            <Suspense fallback={<Loader />}>
              <SetupOrgRoute />
            </Suspense>
          }
        />
        <Route
          path="/settings/team-members"
          element={
            <Suspense fallback={<Loader />}>
              <TeamMembersPanel />
            </Suspense>
          }
        />
        <Route
          path="/settings/data-export"
          element={
            <Suspense fallback={<Loader />}>
              <DataExportPanel />
            </Suspense>
          }
        />
        <Route
          path="/settings/slack"
          element={
            <Suspense fallback={<Loader />}>
              <SlackPanel />
            </Suspense>
          }
        />
        <Route
          path="/settings/review-cycles"
          element={
            <Suspense fallback={<Loader />}>
              <ReviewCyclesPanel />
            </Suspense>
          }
        />
        <Route
          path="/contracts/:id/sign"
          element={
            <Suspense fallback={<Loader />}>
              <ContractSignPage />
            </Suspense>
          }
        />
        <Route
          path="/settings/contracts"
          element={
            <Suspense fallback={<Loader />}>
              <ContractsPanel />
            </Suspense>
          }
        />
        {/*
         * Legacy data-import route — superseded by /settings/setup-
         * organization per org-setup-flow-spec.md §11. We redirect so
         * any saved/bookmarked links from the T-116 rollout still land
         * on the new canonical surface.
         */}
        <Route
          path="/settings/data-import"
          element={<Navigate to="/settings/setup-organization" replace />}
        />
        {/*
         * Catch-all 404 LIVES INSIDE the AppShell wrapper so the
         * sidebar + header + active-nav indicator still render around
         * the broken URL (spec §6.1). Per spec the URL stays unchanged
         * — the prior behavior of <Navigate to="/" replace> hid the
         * broken URL from the user, making it impossible to copy for
         * support. Now `/anything-broken` shows the proper 404 page
         * with the URL preserved.
         */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
